/**
 * Plain-English rendering of qualification reasons.
 *
 * The worker stores its reasons in a compact rule shorthand — "+2
 * years_experience gte 2", "Missing required answer: has_own_tools", a
 * knockout's admin-written reason, and a "Score 8/8 (…)" line — and joins
 * them into `qualification_reason` with "; ". The API string is left exactly
 * as it is; this module only changes how a recruiter reads it, resolving
 * question keys to the role's prompts when the screening config is known and
 * falling back to the key (underscores as spaces) when it is not.
 */
import type { LeadDetail } from '@/features/pipeline/types';
import { screeningFields } from './normalize';
import { RULE_OPS, type RuleOp, type RuleValue, type ScreeningConfig, type ScreeningQuestion } from './types';

export type QuestionLookup = ReadonlyMap<string, ScreeningQuestion>;
type ConfigLike = Pick<ScreeningConfig, 'questions'> & Partial<Pick<ScreeningConfig, 'rules'>>;

const OPS: ReadonlySet<string> = new Set(RULE_OPS.map((o) => o.value));
const KEY_RE = /^[a-z][a-z0-9_]*$/;
const SCORE_RE = /^([+-]\d+(?:\.\d+)?) (\S+) (\S+)(?: ([\s\S]*))?$/;
const MISSING_RE = /^Missing required answers?: (.+)$/;
const KNOCKOUT_FALLBACK_RE = /^Knockout: (\S+)$/;

/** eq "is", neq "is not", gte "is at least"… — the words a recruiter reads. */
const OP_WORDS: Record<RuleOp, string> = {
  eq: 'is',
  neq: 'is not',
  gt: 'is more than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  in: 'is one of',
  contains: 'includes',
  truthy: 'is yes',
  falsy: 'is no',
};

export function questionMap(config: Pick<ScreeningConfig, 'questions'> | null | undefined): QuestionLookup {
  return new Map((config?.questions ?? []).map((q) => [q.key, q]));
}

function keyWords(field: string): string {
  const words = field.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The question's prompt without its question mark; the key as words when unknown. */
export function fieldLabel(questions: QuestionLookup, field: string): string {
  const prompt = questions.get(field)?.prompt?.trim();
  return prompt ? prompt.replace(/\?+$/, '').trim() : keyWords(field);
}

function opWords(op: RuleOp, question: ScreeningQuestion | undefined): string {
  // "is yes" only makes sense for a yes/no question; for text, number and
  // choice questions truthy/falsy mean "an answer was (not) given".
  if (question && question.type !== 'boolean') {
    if (op === 'truthy') return 'was answered';
    if (op === 'falsy') return 'was not answered';
  }
  return OP_WORDS[op];
}

/** "How many years have you worked as a technician is at least 2". */
export function describeRule(questions: QuestionLookup, field: string, op: RuleOp, value: RuleValue): string {
  const needsValue = op !== 'truthy' && op !== 'falsy';
  const v = Array.isArray(value) ? value.join(', ') : value === null ? '' : String(value);
  return `${fieldLabel(questions, field)} ${opWords(op, questions.get(field))}${needsValue && v ? ` ${v}` : ''}`;
}

function pointsLabel(points: string): string {
  const n = Math.abs(Number(points));
  return `(${points} point${n === 1 ? '' : 's'})`;
}

function shorthandValue(raw: string | undefined): RuleValue {
  if (raw === undefined || raw === '') return null;
  return raw.includes('|') ? raw.split('|').map((s) => s.trim()).filter(Boolean) : raw;
}

/** One worker (or Try-it) reason as the recruiter should read it. */
export function humanizeReason(reason: string, config: ConfigLike | null | undefined): string {
  const r = reason.trim();
  const questions = questionMap(config);

  const missing = MISSING_RE.exec(r);
  if (missing) {
    const keys = missing[1]!.split(',').map((s) => s.trim()).filter(Boolean);
    if (keys.length && keys.every((k) => KEY_RE.test(k))) {
      const labels = keys.map((k) => questions.get(k)?.prompt?.trim() || k.replace(/_/g, ' '));
      return `Missing required answer${keys.length > 1 ? 's' : ''}: ${labels.join('; ')}`;
    }
    return r;
  }

  const scored = SCORE_RE.exec(r);
  if (scored && KEY_RE.test(scored[2]!) && OPS.has(scored[3]!)) {
    const [, points, field, op, value] = scored;
    return `${describeRule(questions, field!, op as RuleOp, shorthandValue(value))} ${pointsLabel(points!)}`;
  }

  const knockoutFallback = KNOCKOUT_FALLBACK_RE.exec(r);
  if (knockoutFallback && KEY_RE.test(knockoutFallback[1]!)) {
    const field = knockoutFallback[1]!;
    const rule = config?.rules?.knockouts.find((k) => k.field === field);
    return `Knocked out: ${rule ? describeRule(questions, field, rule.op, rule.value) : fieldLabel(questions, field)}`;
  }

  if (config?.rules?.knockouts.some((k) => k.reason.trim() === r)) return `Knocked out: ${r}`;
  return r;
}

export function humanizeReasons(reasons: readonly string[], config: ConfigLike | null | undefined): string[] {
  return reasons.map((r) => humanizeReason(r, config));
}

/**
 * The reasons behind `lead.qualification`: the analysed call's list when the
 * worker stored one, else the joined `qualification_reason` split back up.
 */
export function leadReasons(lead: Pick<LeadDetail, 'ai_fields_json' | 'qualification_reason'>): string[] {
  const stored = screeningFields(lead)?.reasons ?? [];
  if (stored.length) return stored;
  if (!lead.qualification_reason) return [];
  return lead.qualification_reason.split(/;\s+/).map((s) => s.trim()).filter(Boolean);
}
